(function () {
  'use strict';

  if (window.StackhouseUploadPolicy) {
    return;
  }

  var DEFAULT_API_BASE = 'https://backend.thestackhouse.com';
  var POLICY_PATH = '/api/upload-policy/';
  var VALIDATION_PATH = '/api/validate-image-quick/';
  var POLICY_LOAD_TIMEOUT_MS = 8000;
  var POLICY_LOAD_MAX_ATTEMPTS = 2;
  var POLICY_LOAD_RETRY_DELAY_MS = 500;
  var IMAGE_VALIDATION_MIN_TIMEOUT_MS = 60000;
  var IMAGE_VALIDATION_MAX_TIMEOUT_MS = 900000;
  var IMAGE_VALIDATION_IDLE_TIMEOUT_MS = 30000;
  var IMAGE_VALIDATION_MAX_ATTEMPTS = 2;
  var IMAGE_VALIDATION_RETRY_DELAY_MS = 500;
  var DEFAULT_VALIDATION_CHUNK_BYTES = 1024 * 1024;
  var HDR_VALIDATION_TAIL_BYTES = 1024 * 1024;
  // Adobe TIFF exports often place the first image directory near the file end.
  // Match the established legacy validator cap so ordinary TIFFs include it.
  var TIFF_VALIDATION_CHUNK_BYTES = 50 * 1024 * 1024;
  var VALIDATION_UNAVAILABLE_MESSAGE = "We couldn't validate this image. Please check your connection and try again.";
  var UPLOAD_POLICY_SUMMARY = 'JPEG, PNG, or TIFF up to 1 GB.';
  var TRANSPARENCY_NOTICE = 'This file contains transparency. Transparent areas appear white in the preview. We do not print white ink, so fully transparent areas show the natural color of the paper.';
  var policyPromise = null;
  var loadedPolicy = null;

  function apiBase() {
    return String(window.stackhouseUploadPolicyApiBase || DEFAULT_API_BASE).replace(/\/+$/, '');
  }

  function unavailableResult(error) {
    var requestName = error && error.requestName || 'validation';
    var errorCode = error && error.code || (
      requestName === 'policy'
        ? 'UPLOAD_POLICY_UNAVAILABLE'
        : 'UPLOAD_VALIDATION_UNAVAILABLE'
    );
    return {
      valid: false,
      error_type: /TIMEOUT$/.test(errorCode) ? 'validation_timeout' : 'validation_unavailable',
      error_code: errorCode,
      error: error && error.message || VALIDATION_UNAVAILABLE_MESSAGE,
      retryable: Boolean(error && error.retryable),
      retry_count: Math.max(0, Number(error && error.attemptCount || 1) - 1),
      duration_ms: Math.max(0, Number(error && error.durationMs || 0))
    };
  }

  function wait(milliseconds) {
    return new Promise(function (resolve) {
      window.setTimeout(resolve, milliseconds);
    });
  }

  function isAbortError(error) {
    var message = String(error && error.message || '');
    return Boolean(
      error
      && (
        error.name === 'AbortError'
        || /(?:fetch is |signal is )?aborted|aborterror/i.test(message)
      )
    );
  }

  function isNetworkError(error) {
    var message = String(error && error.message || '');
    return Boolean(
      error
      && !error.statusCode
      && (
        error.name === 'TypeError'
        || /network|load failed|failed to fetch|offline/i.test(message)
      )
    );
  }

  function normalizedTransportError(error, config, timedOut) {
    var normalized;
    var requestName = config && config.requestName || 'validation';

    if (timedOut || isAbortError(error)) {
      normalized = new Error(VALIDATION_UNAVAILABLE_MESSAGE);
      normalized.code = requestName === 'policy'
        ? 'UPLOAD_POLICY_TIMEOUT'
        : 'UPLOAD_VALIDATION_TIMEOUT';
      normalized.retryable = true;
      normalized.requestName = requestName;
      return normalized;
    }
    if (isNetworkError(error)) {
      normalized = new Error(VALIDATION_UNAVAILABLE_MESSAGE);
      normalized.code = requestName === 'policy'
        ? 'UPLOAD_POLICY_NETWORK_FAILED'
        : 'UPLOAD_VALIDATION_NETWORK_FAILED';
      normalized.retryable = true;
      normalized.requestName = requestName;
      return normalized;
    }
    if (error) {
      error.requestName = error.requestName || requestName;
    }
    return error;
  }

  function requestValidationJson(url, options, config) {
    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      var idleTimer;
      var settled = false;
      function finish(error, data) {
        if (settled) return;
        settled = true;
        window.clearTimeout(idleTimer);
        if (error) reject(error); else resolve(data);
      }
      function timeout() {
        var error = normalizedTransportError(null, config, true);
        error.message = 'The connection stopped responding while checking your image. Your previous artwork is still available. Please retry.';
        finish(error);
        xhr.abort();
      }
      function heartbeat() {
        window.clearTimeout(idleTimer);
        idleTimer = window.setTimeout(timeout, IMAGE_VALIDATION_IDLE_TIMEOUT_MS);
      }
      xhr.open('POST', url, true);
      xhr.timeout = config.timeoutMs;
      xhr.upload.onprogress = function (event) {
        heartbeat();
        if (config.onProgress && event.lengthComputable) config.onProgress(Math.round(event.loaded / event.total * 100));
      };
      xhr.upload.onload = heartbeat;
      xhr.onprogress = heartbeat;
      xhr.ontimeout = timeout;
      xhr.onerror = function () { finish(normalizedTransportError(new TypeError('Network request failed'), config, false)); };
      xhr.onabort = function () { finish(normalizedTransportError(new Error('Aborted'), config, true)); };
      xhr.onload = function () {
        var data;
        try { data = JSON.parse(xhr.responseText); } catch (_) { data = {}; }
        if (xhr.status < 200 || xhr.status >= 300) {
          var error = new Error(data.error || 'Image validation is temporarily unavailable.');
          error.responseData = data;
          error.statusCode = xhr.status;
          error.code = data.error_code || data.code || '';
          error.retryable = xhr.status === 408 || xhr.status === 429 || xhr.status >= 500;
          finish(error);
        } else { finish(null, data); }
      };
      heartbeat();
      xhr.send(options.body);
    });
  }

  function requestPolicyWithXhr(url, config) {
    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      var settled = false;
      var timer;
      function finish(error, data) {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        if (error) reject(normalizedTransportError(error, config, false)); else resolve(data);
      }
      function timeout() {
        finish(normalizedTransportError(null, config, true));
        xhr.abort();
      }
      // XHR has no fetch cache mode. A fresh opaque query value prevents reuse
      // of a cached policy while keeping the request CORS-safelisted.
      xhr.open('GET', url + (url.indexOf('?') === -1 ? '?' : '&')
        + '_stackhouse_policy_retry=' + encodeURIComponent(config.policyRequestId), true);
      xhr.withCredentials = false;
      xhr.timeout = config.timeoutMs;
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.ontimeout = timeout;
      xhr.onerror = function () { finish(normalizedTransportError(new TypeError('Network request failed'), config, false)); };
      xhr.onabort = function () { finish(normalizedTransportError(new Error('Aborted'), config, true)); };
      xhr.onload = function () {
        var data;
        try { data = JSON.parse(xhr.responseText); } catch (_) { data = {}; }
        if (xhr.status < 200 || xhr.status >= 300) {
          var error = new Error(data.error || 'Upload requirements are temporarily unavailable.');
          error.responseData = data;
          error.statusCode = xhr.status;
          error.code = data.error_code || data.code || '';
          error.retryable = xhr.status === 408 || xhr.status === 429 || xhr.status >= 500;
          finish(error);
        } else { finish(null, data); }
      };
      timer = window.setTimeout(timeout, config.timeoutMs);
      xhr.send();
    });
  }

  function requestJson(url, options, config) {
    config = config || {};
    if (config.requestName === 'validation') return requestValidationJson(url, options, config);
    if (config.policyXhr) return requestPolicyWithXhr(url, config);
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timedOut = false;
    var timeoutMs = Number(config.timeoutMs) || POLICY_LOAD_TIMEOUT_MS;
    var timeoutId;
    var deadline = new Promise(function (_resolve, reject) {
      timeoutId = window.setTimeout(function () {
        timedOut = true;
        reject(normalizedTransportError(null, config, true));
        if (controller) controller.abort();
      }, timeoutMs);
    });
    var requestOptions = Object.assign({}, options || {});

    if (controller) {
      requestOptions.signal = controller.signal;
    }

    var request = Promise.resolve().then(function () { return fetch(url, requestOptions); }).then(function (response) {
      return response.json().catch(function () {
        return {};
      }).then(function (data) {
        if (!response.ok) {
          var error = new Error(data.error || 'Upload requirements are temporarily unavailable.');
          error.responseData = data;
          error.statusCode = response.status;
          error.code = data.error_code || data.code || '';
          error.retryable = response.status === 408 || response.status === 429 || response.status >= 500;
          throw error;
        }
        return data;
      });
    });
    return Promise.race([request, deadline]).catch(function (error) {
      throw normalizedTransportError(error, config, timedOut);
    }).finally(function () {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    });
  }

  function requestJsonWithRetry(url, options, config) {
    config = config || {};
    var maxAttempts = Math.max(1, Number(config.maxAttempts) || 1);
    var retryDelayMs = Math.max(0, Number(config.retryDelayMs) || 0);
    var startedAt = Date.now();
    var policyLoadId = config.requestName === 'policy' ? correlationId() : '';

    function correlationId() {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (character) {
        var value = Math.floor(Math.random() * 16);
        return (character === 'x' ? value : (value & 3) | 8).toString(16);
      });
    }

    function tracePolicy(requestId, event, status, attemptNumber, error) {
      if (!policyLoadId) return;
      try {
        var telemetry = window.StackhouseOperationalTelemetry;
        if (telemetry && typeof telemetry.emit === 'function') telemetry.emit({
          workflow: 'upload_request_trace', stage: 'policy_read', operation: 'policy_read',
          workflow_instance_id: policyLoadId, attempt_id: requestId, event: event, status: status,
          severity: status === 'failed' ? 'warning' : 'info',
          error_code: error && error.code || '', status_code: error && error.statusCode,
          retry_count: attemptNumber - 1, duration_ms: elapsed(),
          safe_context: { endpoint_family: 'upload_policy', request_id: requestId, retry_count: attemptNumber - 1 }
        });
      } catch (_traceError) {}
    }

    function elapsed() {
      return Math.max(0, Date.now() - startedAt);
    }

    function attempt(attemptNumber, previousError) {
      var requestOptions = typeof options === 'function' ? options() : options;
      var requestId = policyLoadId ? correlationId() : '';
      // This helper records both transports under one bounded policy load.
      if (policyLoadId) requestOptions = Object.assign({}, requestOptions, { stackhouseSkipOperationalTelemetry: true });
      var attemptConfig = Object.assign({}, config, {
        policyRequestId: requestId,
        policyXhr: config.requestName === 'policy' && typeof XMLHttpRequest === 'function'
          && previousError && (previousError.code === 'UPLOAD_POLICY_NETWORK_FAILED'
            || previousError.code === 'UPLOAD_POLICY_TIMEOUT')
      });

      tracePolicy(requestId, 'upload_request_started', 'started', attemptNumber);
      return requestJson(url, requestOptions, attemptConfig).then(function (result) {
        tracePolicy(requestId, 'upload_request_completed', 'succeeded', attemptNumber);
        if (result && typeof result === 'object') {
          result.client_attempt_count = attemptNumber;
          result.client_duration_ms = elapsed();
        }
        return result;
      }).catch(function (error) {
        tracePolicy(requestId, 'upload_request_failed', 'failed', attemptNumber, error);
        if (!error || !error.retryable || attemptNumber >= maxAttempts) {
          if (error) {
            error.attemptCount = attemptNumber;
            error.durationMs = elapsed();
          }
          throw error;
        }
        return wait(retryDelayMs).then(function () {
          return attempt(attemptNumber + 1, error);
        });
      });
    }

    return attempt(1);
  }

  function normalizePolicy(policy) {
    var formats = Array.isArray(policy && policy.formats) ? policy.formats : [];
    var extensions = [];
    var mimeTypes = [];

    formats.forEach(function (format) {
      (format.extensions || []).forEach(function (extension) {
        extensions.push(String(extension).toLowerCase().replace(/^\./, ''));
      });
      (format.mime_types || []).forEach(function (mimeType) {
        mimeTypes.push(String(mimeType).toLowerCase());
      });
    });

    if (
      !policy ||
      [1, 2].indexOf(Number(policy.schema_version)) === -1 ||
      !policy.policy_version ||
      !extensions.length ||
      !Number(policy.max_file_size_bytes) ||
      !Number(policy.max_megapixels) ||
      !policy.ppi ||
      Number(policy.ppi.minimum) !== 150 ||
      Number(policy.ppi.recommended) !== 300 ||
      (policy.ppi.tolerance != null && (
        !Number.isFinite(Number(policy.ppi.tolerance)) ||
        Number(policy.ppi.tolerance) < 0 ||
        Number(policy.ppi.tolerance) > 1
      ))
    ) {
      throw new Error('Upload requirements returned an unsupported response.');
    }

    var normalizedPolicy = Object.assign({}, policy, {
      extensions: extensions,
      mime_types: mimeTypes,
      summary: extensions.indexOf('heic') !== -1 ? 'JPEG, PNG, TIFF, or HEIC up to 1 GB.' : UPLOAD_POLICY_SUMMARY
    });

    if (policy.transparency === 'accepted_on_white') {
      normalizedPolicy.detected_file_notices = Object.assign({}, policy.detected_file_notices || {}, {
        transparency: TRANSPARENCY_NOTICE
      });
    }

    return normalizedPolicy;
  }

  function ready() {
    if (!policyPromise) {
      policyPromise = requestJsonWithRetry(apiBase() + POLICY_PATH, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        credentials: 'omit'
      }, {
        requestName: 'policy',
        timeoutMs: POLICY_LOAD_TIMEOUT_MS,
        maxAttempts: POLICY_LOAD_MAX_ATTEMPTS,
        retryDelayMs: POLICY_LOAD_RETRY_DELAY_MS
      }).then(normalizePolicy).then(function (policy) {
        loadedPolicy = policy;
        return policy;
      }).catch(function (error) {
        policyPromise = null;
        throw error;
      });
    }
    return policyPromise;
  }

  function extensionFor(file) {
    var match = String(file && file.name || '').toLowerCase().match(/\.([a-z0-9]+)$/);
    return match ? match[1] : '';
  }

  function validateDimensions(dimensions, policy) {
    policy = policy || loadedPolicy;
    if (!policy || ![policy.max_width, policy.max_height, policy.max_pixels].every(function (value) {
      return Number.isFinite(Number(value)) && Number(value) > 0;
    })) {
      return unavailableResult();
    }
    var width = Number(dimensions && dimensions.width);
    var height = Number(dimensions && dimensions.height);
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
      return unavailableResult();
    }
    if (width > Number(policy.max_width) || height > Number(policy.max_height) || width * height > Number(policy.max_pixels)) {
      var managed = policy.rejection_messages && policy.rejection_messages.pixel_limit_exceeded || {};
      return {
        valid: false,
        error_code: 'PIXEL_LIMIT_EXCEEDED',
        error_type: 'too_large_dimensions',
        error: managed.error || 'This image exceeds the upload dimensions. Resize a copy and upload it again. Keep your original.',
        help_link: managed.help_link || ''
      };
    }
    return { valid: true };
  }

  function validationChunkSize(file) {
    var extension = extensionFor(file);
    var mimeType = String(file && file.type || '').toLowerCase();
    var isTiff = extension === 'tif'
      || extension === 'tiff'
      || mimeType === 'image/tif'
      || mimeType === 'image/tiff'
      || mimeType === 'image/x-tiff';
    var limit = isTiff ? TIFF_VALIDATION_CHUNK_BYTES : DEFAULT_VALIDATION_CHUNK_BYTES;

    return Math.min(Number(file && file.size || 0), limit);
  }

  function validationTail(file) {
    var extension = extensionFor(file);
    var mimeType = String(file && file.type || '').toLowerCase();
    var isJpeg = extension === 'jpg' || extension === 'jpeg' || /^image\/(?:p?jpeg|jpg)$/.test(mimeType);
    var fileSize = Number(file && file.size || 0);
    var prefixSize = validationChunkSize(file);
    if (!isJpeg || fileSize <= prefixSize) return null;
    return file.slice(Math.max(prefixSize, fileSize - HDR_VALIDATION_TAIL_BYTES), fileSize);
  }

  function noticeMessage(type) {
    var policyNotices = loadedPolicy && loadedPolicy.detected_file_notices || {};
    var defaults = {
      transparency: TRANSPARENCY_NOTICE,
      ultrahdr: 'This HDR JPEG includes an HDR gain map. We will print the standard SDR image shown in your preview.',
      '16_bit': '16-bit RGB detected. Full precision will be retained through production.'
    };
    if (policyNotices[type]) return policyNotices[type];
    if (type === 'transparency' && loadedPolicy && loadedPolicy.transparency !== 'accepted_on_white') return '';
    return defaults[type] || '';
  }

  function sourceObject(value) {
    var source = value && typeof value === 'object' ? value : {};
    if (source.source && typeof source.source === 'object') source = source.source;
    if (source.backend && typeof source.backend === 'object') source = source.backend;
    if (source.source && typeof source.source === 'object') source = source.source;
    return source;
  }

  function sourceTraits(value) {
    var source = sourceObject(value);
    var metadata = Object.assign({}, source.image_metadata || {}, source.metadata || {});

    function firstDefined() {
      for (var index = 0; index < arguments.length; index += 1) {
        if (arguments[index] !== undefined && arguments[index] !== null && arguments[index] !== '') {
          return arguments[index];
        }
      }
      return undefined;
    }

    function isTrue(candidate) {
      return candidate === true || candidate === 1 || String(candidate || '').toLowerCase() === 'true';
    }

    return {
      format: String(firstDefined(source.source_format, source.original_format, metadata.source_format, metadata.original_format, source.format, '')).toLowerCase(),
      variant: String(firstDefined(source.variant, source.source_variant, metadata.source_variant, 'standard')).toLowerCase(),
      has_gain_map: isTrue(firstDefined(source.has_gain_map, source.source_has_gain_map, metadata.source_has_gain_map, false)),
      has_layers: isTrue(firstDefined(source.source_has_layers, metadata.source_has_layers, false)),
      has_alpha: isTrue(firstDefined(source.has_alpha, source.source_has_alpha, metadata.source_has_alpha, metadata.has_alpha, false)),
      bit_depth: Number(firstDefined(source.bit_depth, source.source_bit_depth, metadata.source_bit_depth, metadata.bit_depth, 0))
    };
  }

  function noticesForSource(value) {
    var source = sourceTraits(value);
    var notices = [];
    var info = sourceObject(value);
    if ((info.metadata || {}).source_is_heic && loadedPolicy && loadedPolicy.heic_notice) notices.push(loadedPolicy.heic_notice);
    if (source.has_layers && loadedPolicy && loadedPolicy.preparation_notice) notices.push(loadedPolicy.preparation_notice);
    if (source.has_alpha === true) notices.push({ type: 'transparency', message: noticeMessage('transparency') });
    if (source.variant === 'ultrahdr' || source.has_gain_map === true) {
      notices.push({ type: 'ultrahdr', message: noticeMessage('ultrahdr') });
    }
    if (Number(source.bit_depth) === 16) notices.push({ type: '16_bit', message: noticeMessage('16_bit') });
    return notices;
  }

  function sourceIdentity(value) {
    var outer = value && typeof value === 'object' ? value : {};
    var source = sourceObject(value);
    var candidates = [outer, outer.backend || {}, outer.source || {}, source];
    var identity = '';
    candidates.some(function (candidate) {
      var id = candidate.asset_id || candidate.unique_id || candidate.uniqueId || candidate.image_id || candidate.id || candidate.order;
      if (typeof id === 'string' || typeof id === 'number') {
        identity = String(id);
        return Boolean(identity);
      }
      return false;
    });
    if (!identity) {
      candidates.some(function (candidate) {
        if (typeof candidate.original_file !== 'string' || !candidate.original_file) return false;
        identity = candidate.original_file.split('?')[0];
        return true;
      });
    }
    return identity || String(source.original_file_name || source.file_name || source.name || outer.name || '');
  }

  function renderFileDetails(value, notices, node, details) {
    var traits = sourceTraits(value);
    var format = {
      jpeg: 'JPEG', jpg: 'JPEG', 'image/jpeg': 'JPEG',
      png: 'PNG', 'image/png': 'PNG',
      tif: 'TIFF', tiff: 'TIFF', 'image/tiff': 'TIFF',
      heic: 'HEIC', heif: 'HEIC', 'image/heic': 'HEIC', 'image/heif': 'HEIC'
    }[traits.format];
    var has16BitNotice = notices.some(function (notice) { return notice.type === '16_bit'; });
    var bitDepth = traits.bit_depth || (has16BitNotice ? 16 : 0);
    var rows = [];
    var metadata = [];

    if (notices.length) {
      if (typeof format === 'string') metadata.push(format);
      if (bitDepth === 8 || bitDepth === 16) {
        metadata.push('RGB', bitDepth + '-bit');
      }
      if (metadata.length) rows.push({ message: metadata.join(' · ') });
      notices.forEach(function (notice) {
        if (notice.type === '16_bit') return;
        if (notice.type === 'transparency' && loadedPolicy && loadedPolicy.transparency === 'accepted_on_white') {
          rows.push({ message: details.getAttribute('data-transparency-copy') || 'Transparency: Previewed on white. Fully transparent areas show the paper color in print.' });
        } else if (notice.type === 'ultrahdr') {
          rows.push({ message: details.getAttribute('data-ultrahdr-copy') || 'HDR JPEG: We print the standard SDR image shown in your preview.' });
        } else {
          rows.push({ message: notice.message, help_link: notice.help_link });
        }
      });
    }

    var key = sourceIdentity(value);
    var content = JSON.stringify(rows);
    if (!rows.length || details.getAttribute('data-file-details-key') !== key) {
      details.open = false;
      details.setAttribute('data-file-details-key', key);
    }
    if (details.getAttribute('data-file-details-content') !== content) {
      details.setAttribute('data-file-details-content', content);
      node.textContent = '';
      rows.forEach(function (row) {
        var paragraph = (node.ownerDocument || document).createElement('p');
        if (row.help_link) appendNotice(paragraph, row);
        else paragraph.textContent = row.message;
        node.appendChild(paragraph);
      });
    }
    node.hidden = rows.length === 0;
    details.hidden = rows.length === 0;
  }

  function appendNotice(node, notice) {
    var paragraph = document.createElement('span');
    paragraph.textContent = notice.message;
    node.appendChild(paragraph);
    try {
      var url = new URL(notice.help_link || '');
      if (url.protocol !== 'https:' || url.username || url.password) return;
      var link = document.createElement('a');
      link.textContent = 'More info';
      link.href = url.href;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.className = 'stackhouse-upload-notice__link';
      node.appendChild(link);
    } catch (_error) { /* Missing or unsafe links are not rendered. */ }
  }

  function renderUploadError(error) {
    var candidates = document.querySelectorAll('[data-stackhouse-upload-policy-summary], [data-stackhouse-file-details] > summary, [data-stackhouse-upload-notices]');
    var anchor = Array.prototype.find.call(candidates, function (candidate) {
      return candidate.getClientRects().length > 0;
    });
    if (!anchor) { window.alert(error.message); return; }
    var node = document.querySelector('[data-stackhouse-upload-error]');
    if (!node) {
      node = document.createElement('div');
      node.setAttribute('data-stackhouse-upload-error', '');
      node.setAttribute('role', 'alert');
      node.className = 'stackhouse-upload-notice';
    }
    var details = anchor.closest('[data-stackhouse-file-details]');
    var outside = details || anchor;
    outside.parentNode.insertBefore(node, outside);
    node.textContent = '';
    appendNotice(node, {message: error.message, help_link: error.helpLink || error.help_link});
    node.hidden = false;
  }

  function renderDetectedNotices(value, scope) {
    var notices = Array.isArray(value && value.notices)
      ? value.notices.map(function (notice) {
        if (
          notice
          && notice.type === 'transparency'
          && loadedPolicy
          && loadedPolicy.transparency === 'accepted_on_white'
        ) {
          return Object.assign({}, notice, { message: noticeMessage('transparency') });
        }
        return notice;
      })
      : noticesForSource(value && value.source ? value.source : value);
    noticesForSource(value).forEach(function (notice) {
      if (!notices.some(function (existing) { return (existing.code || existing.type) === (notice.code || notice.type); })) notices.push(notice);
    });
    notices = notices.filter(function (notice) {
      return notice && typeof notice.message === 'string' && notice.message.trim();
    });
    var root = scope && typeof scope.querySelectorAll === 'function' ? scope : document;
    root.querySelectorAll('[data-stackhouse-upload-error]').forEach(function (node) { node.textContent = ''; node.hidden = true; });

    root.querySelectorAll('[data-stackhouse-upload-notices]').forEach(function (node) {
      var details = typeof node.closest === 'function' && node.closest('[data-stackhouse-file-details]');
      if (details) {
        renderFileDetails(value, notices, node, details);
        return;
      }
      node.textContent = '';
      notices.forEach(function (notice) { appendNotice(node, notice); });
      node.hidden = notices.length === 0;
    });
    return notices;
  }

  function validateMetadata(file, policy) {
    var extension;
    var mimeType;

    if (!file) {
      return { valid: false, error_type: 'no_file', error: 'Choose an image file to upload.' };
    }

    extension = extensionFor(file);
    mimeType = String(file.type || '').toLowerCase();

    if (policy.extensions.indexOf(extension) === -1) {
      return {
        valid: false,
        error_type: 'unsupported_format',
        error: 'Unsupported file format. Upload a JPEG, PNG, or TIFF file.'
      };
    }
    if (
      mimeType &&
      mimeType !== 'application/octet-stream' &&
      policy.mime_types.indexOf(mimeType) === -1
    ) {
      return {
        valid: false,
        error_type: 'unsupported_format',
        error: 'Unsupported file format. Upload a JPEG, PNG, or TIFF file.'
      };
    }
    if (file.size < Number(policy.min_file_size_bytes)) {
      return {
        valid: false,
        error_type: 'too_small',
        error: 'That file is too small to be a valid print image.'
      };
    }
    if (file.size > Number(policy.max_file_size_bytes)) {
      return {
        valid: false,
        error_type: 'too_large',
        error: 'Image file is too large. Maximum size is 1 GB.'
      };
    }
    return {
      valid: true,
      policy_version: policy.policy_version,
      policy: policy
    };
  }

  function validateFile(file) {
    return ready().then(function (policy) {
      return validateLocalPixels(file, policy);
    }).catch(function (error) {
      return unavailableResult(error);
    });
  }

  async function localImageDimensions(file) {
    // Read headers only. Uncertain or expensive files still receive worker validation.
    if (!file || typeof file.slice !== 'function') return null;
    var bytesRead = 0;
    var reads = 0;
    var stopped = false;
    var timer;
    var readers = [];
    async function readAt(offset, length) {
      if (stopped || !Number.isSafeInteger(offset) || offset < 0 || length <= 0
          || offset + length > file.size || bytesRead + length > 65536 || ++reads > 96) {
        throw new Error('Header read budget exceeded');
      }
      bytesRead += length;
      var blob = file.slice(offset, offset + length);
      var buffer;
      if (typeof blob.arrayBuffer === 'function') {
        buffer = await blob.arrayBuffer();
      } else if (typeof window.FileReader === 'function') {
        buffer = await new Promise(function (resolve, reject) {
          var reader = new window.FileReader();
          readers.push(reader);
          reader.onload = function () { resolve(reader.result); };
          reader.onerror = reader.onabort = function () { reject(new Error('Header read unavailable')); };
          reader.readAsArrayBuffer(blob);
        });
      } else {
        throw new Error('Header read unavailable');
      }
      if (stopped || buffer.byteLength !== length) throw new Error('Incomplete header');
      return new DataView(buffer);
    }
    function uint64(view, offset, little) {
      var low = view.getUint32(offset + (little ? 0 : 4), little);
      var high = view.getUint32(offset + (little ? 4 : 0), little);
      var value = high * 4294967296 + low;
      if (!Number.isSafeInteger(value)) throw new Error('Unsafe header integer');
      return value;
    }
    async function inspect() {
      var header = await readAt(0, 24);
      var width, height;
      if (header.getUint32(0) === 0x89504e47 && header.getUint32(4) === 0x0d0a1a0a
          && header.getUint32(8) === 13 && header.getUint32(12) === 0x49484452) {
        width = header.getUint32(16);
        height = header.getUint32(20);
      } else if (header.getUint16(0) === 0xffd8) {
        var offset = 2;
        while (true) {
          var segment = await readAt(offset, 4);
          if (segment.getUint8(0) !== 255) return null;
          var marker = segment.getUint8(1);
          if (marker === 255) { offset += 1; continue; }
          if (marker === 0xda || marker === 0xd9 || marker === 0) return null;
          if (marker === 1 || (marker >= 0xd0 && marker <= 0xd8)) { offset += 2; continue; }
          var length = segment.getUint16(2);
          if (length < 2 || offset + 2 + length > file.size) return null;
          if ([0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf].indexOf(marker) !== -1) {
            if (length < 8) return null;
            var frame = await readAt(offset + 4, 6);
            height = frame.getUint16(1);
            width = frame.getUint16(3);
            break;
          }
          offset += 2 + length;
        }
      } else if (header.getUint16(0) === 0x4949 || header.getUint16(0) === 0x4d4d) {
        var little = header.getUint16(0) === 0x4949;
        var magic = header.getUint16(2, little);
        var big = magic === 43;
        if (magic !== 42 && !big) return null;
        if (big && (header.getUint16(4, little) !== 8 || header.getUint16(6, little) !== 0)) return null;
        var directory = big ? uint64(header, 8, little) : header.getUint32(4, little);
        if (directory < (big ? 16 : 8)) return null;
        var countSize = big ? 8 : 2;
        var entrySize = big ? 20 : 12;
        var countHeader = await readAt(directory, countSize);
        var count = big ? uint64(countHeader, 0, little) : countHeader.getUint16(0, little);
        if (!count || count > 2048) return null;
        var table = await readAt(directory + countSize, count * entrySize);
        var dimensions = {};
        // Only the primary IFD describes the composite checked by the TIFF worker.
        // Do not walk layers, thumbnails, SubIFDs, strips, or pixel payloads.
        for (var index = 0; index < count; index += 1) {
          var start = index * entrySize;
          var tag = table.getUint16(start, little);
          if (tag !== 256 && tag !== 257) continue;
          if (Object.prototype.hasOwnProperty.call(dimensions, tag)) return null;
          var type = table.getUint16(start + 2, little);
          var values = big ? uint64(table, start + 4, little) : table.getUint32(start + 4, little);
          if (values !== 1 || [3, 4, 16].indexOf(type) === -1 || (!big && type === 16)) return null;
          var valueOffset = start + (big ? 12 : 8);
          dimensions[tag] = type === 3 ? table.getUint16(valueOffset, little)
            : type === 4 ? table.getUint32(valueOffset, little) : uint64(table, valueOffset, little);
        }
        width = dimensions[256];
        height = dimensions[257];
      }
      return Number.isSafeInteger(width) && width > 0 && Number.isSafeInteger(height) && height > 0
        ? { width: width, height: height } : null;
    }
    try {
      return await Promise.race([
        inspect(),
        new Promise(function (resolve) { timer = window.setTimeout(function () { resolve(null); }, 2000); })
      ]);
    } catch (_error) {
      return null;
    } finally {
      stopped = true;
      window.clearTimeout(timer);
      readers.forEach(function (reader) { if (reader.readyState === 1) reader.abort(); });
    }
  }

  async function validateLocalPixels(file, policy) {
    var metadata = validateMetadata(file, policy);
    if (!metadata.valid) return metadata;
    var dimensions = await localImageDimensions(file);
    if (!dimensions) return metadata;
    var result = validateDimensions(dimensions, policy);
    return result.valid ? metadata : result;
  }

  function validateBeforeUpload(file, options) {
    options = options || {};
    return ready().then(function (policy) {
      var localResult = validateMetadata(file, policy);
      if (!localResult.valid) {
        return localResult;
      }
      if (policy.transport === 'upload_v2') return validateLocalPixels(file, policy);

      return requestJsonWithRetry(apiBase() + VALIDATION_PATH, function () {
        var chunk = file.slice(0, validationChunkSize(file));
        var tail = validationTail(file);
        var formData = new FormData();

        formData.append('chunk', chunk, file.name);
        if (tail) formData.append('tail', tail, file.name + '.tail');
        formData.append('file_name', file.name);
        formData.append('file_size', String(file.size));
        formData.append('upload_policy_version', policy.policy_version);

        return {
          method: 'POST',
          body: formData,
          credentials: 'omit'
        };
      }, {
        requestName: 'validation',
        // Budget transfer at 256 kbps plus processing, with both idle and total caps.
        timeoutMs: Math.min(IMAGE_VALIDATION_MAX_TIMEOUT_MS, Math.max(IMAGE_VALIDATION_MIN_TIMEOUT_MS,
          30000 + Math.ceil((validationChunkSize(file) + (validationTail(file) || { size: 0 }).size) / 32768 * 1000))),
        onProgress: options.onProgress,
        maxAttempts: IMAGE_VALIDATION_MAX_ATTEMPTS,
        retryDelayMs: IMAGE_VALIDATION_RETRY_DELAY_MS
      }).then(function (result) {
        if (result.policy_version !== policy.policy_version) {
          return {
            valid: false,
            error_type: 'upload_policy_changed',
            error: 'Upload requirements changed while this page was open. Refresh the page and choose the file again.'
          };
        }
        if (result.valid !== true) {
          renderDetectedNotices([]);
          return Object.assign({
            valid: false,
            error_type: 'validation_failed',
            error: 'The image could not be validated.'
          }, result);
        }
        var normalizedResult = Object.assign({}, result, {
          valid: true,
          policy: policy,
          policy_version: policy.policy_version,
          retry_count: Math.max(0, Number(result.client_attempt_count || 1) - 1),
          duration_ms: Math.max(0, Number(result.client_duration_ms || 0))
        });
        renderDetectedNotices(normalizedResult);
        return normalizedResult;
      });
    }).catch(function (error) {
      if (error && !error.retryable && error.responseData && error.responseData.error) {
        return Object.assign({ valid: false }, error.responseData);
      }
      return unavailableResult(error);
    });
  }

  function ppiRequirements() {
    var ppiPolicy = loadedPolicy && loadedPolicy.ppi ? loadedPolicy.ppi : {};
    var minimum = Number(ppiPolicy.minimum) || 150;
    var tolerance = Number(ppiPolicy.tolerance);
    // An older or unavailable server policy keeps the strict nominal minimum.
    if (!Number.isFinite(tolerance) || tolerance < 0 || tolerance > 1) tolerance = 0;
    return {
      minimum: minimum,
      tolerance: tolerance,
      orderMinimum: minimum - tolerance,
      recommended: Number(ppiPolicy.recommended) || 300
    };
  }

  function qualityForPpi(ppi) {
    var normalized = Number(ppi);
    var ppiPolicy = loadedPolicy && loadedPolicy.ppi ? loadedPolicy.ppi : {};
    var requirements = ppiRequirements();
    var minimum = requirements.orderMinimum;
    var recommended = Number(ppiPolicy.recommended) || 300;
    var warningBand = Array.isArray(ppiPolicy.bands)
      ? ppiPolicy.bands.find(function (band) { return band && band.status === 'warning'; })
      : null;
    var warning = Number(warningBand && warningBand.minimum) || 200;

    if (!Number.isFinite(normalized) || normalized < minimum) {
      return { label: 'Blocked', status: 'blocked', available: false };
    }
    if (normalized < requirements.minimum) {
      return { label: 'Low', status: 'low', available: true, withinTolerance: true };
    }
    if (normalized < warning) {
      return { label: 'Low', status: 'low', available: true };
    }
    if (normalized < recommended) {
      return { label: 'Warning', status: 'warning', available: true };
    }
    return { label: 'Recommended', status: 'recommended', available: true };
  }

  function effectivePpi(imageWidth, imageHeight, printWidth, printHeight) {
    var dimensions = [imageWidth, imageHeight, printWidth, printHeight].map(Number);
    if (dimensions.some(function (value) { return !Number.isFinite(value) || value <= 0; })) {
      return NaN;
    }
    return Math.min(
      dimensions[0] / dimensions[2],
      dimensions[1] / dimensions[3]
    );
  }

  function qualityForDimensions(imageWidth, imageHeight, printWidth, printHeight) {
    var ppi = effectivePpi(imageWidth, imageHeight, printWidth, printHeight);
    return Object.assign({ ppi: ppi }, qualityForPpi(ppi));
  }

  function qualityForBestOrientation(imageWidth, imageHeight, printWidth, printHeight) {
    var direct = qualityForDimensions(imageWidth, imageHeight, printWidth, printHeight);
    var rotated = qualityForDimensions(imageWidth, imageHeight, printHeight, printWidth);
    return direct.ppi >= rotated.ppi ? direct : rotated;
  }

  function createTaskScheduler(concurrency) {
    var limit = Math.max(1, Math.floor(Number(concurrency) || 1));
    var activeCount = 0;
    var pendingTasks = [];

    function drain() {
      while (activeCount < limit && pendingTasks.length) {
        var task = pendingTasks.shift();
        activeCount += 1;
        (function (scheduledTask) {
          Promise.resolve()
            .then(function () {
              return scheduledTask.worker(scheduledTask.item, scheduledTask.index);
            })
            .then(scheduledTask.resolve, scheduledTask.reject)
            .finally(function () {
              activeCount -= 1;
              drain();
            });
        })(task);
      }
    }

    function add(item, worker, index) {
      if (typeof worker !== 'function') {
        return Promise.reject(new TypeError('A queue worker function is required.'));
      }
      var taskPromise = new Promise(function (resolve, reject) {
        pendingTasks.push({
          item: item,
          worker: worker,
          index: index,
          resolve: resolve,
          reject: reject
        });
      });
      drain();
      return taskPromise;
    }

    function addAll(items, worker) {
      return Promise.all(
        Array.from(items || []).map(function (item, index) {
          return add(item, worker, index);
        })
      );
    }

    return {
      add: add,
      addAll: addAll,
      activeCount: function () { return activeCount; },
      queuedCount: function () { return pendingTasks.length; },
      hasPending: function () { return activeCount > 0 || pendingTasks.length > 0; }
    };
  }

  function runWithConcurrency(items, concurrency, worker) {
    return createTaskScheduler(concurrency).addAll(items, worker);
  }

  function applyPolicyToPage(policy) {
    var accept = policy.accept || '';
    document.documentElement.removeAttribute('data-stackhouse-upload-policy');
    document.querySelectorAll('[data-stackhouse-upload-input]').forEach(function (input) {
      input.setAttribute('accept', accept);
      input.setAttribute('data-upload-policy-version', policy.policy_version);
    });
    document.querySelectorAll('[data-stackhouse-upload-format-list]').forEach(function (node) {
      node.textContent = (policy.formats || []).map(function (format) { return format.label; }).join(', ');
    });
    document.querySelectorAll('[data-stackhouse-upload-policy-summary]').forEach(function (node) {
      if (node.getAttribute('data-builder-upload-policy') === 'true') return;
      node.textContent = policy.summary;
    });
  }

  function initializePage() {
    if (!document.querySelectorAll('[data-stackhouse-upload-input], [data-order-file-input], [data-stackhouse-upload-policy-summary]').length) return;
    ready().then(applyPolicyToPage).catch(function () {
      document.documentElement.setAttribute('data-stackhouse-upload-policy', 'unavailable');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializePage, { once: true });
  } else {
    initializePage();
  }

  window.StackhouseUploadPolicy = {
    ready: ready,
    validateFile: validateFile,
    validateDimensions: validateDimensions,
    validateBeforeUpload: validateBeforeUpload,
    noticesForSource: noticesForSource,
    renderDetectedNotices: renderDetectedNotices,
    renderUploadError: renderUploadError,
    effectivePpi: effectivePpi,
    qualityForPpi: qualityForPpi,
    ppiRequirements: ppiRequirements,
    qualityForDimensions: qualityForDimensions,
    qualityForBestOrientation: qualityForBestOrientation,
    createTaskScheduler: createTaskScheduler,
    runWithConcurrency: runWithConcurrency,
    applyPolicyToPage: applyPolicyToPage
  };
})();
